import { useMutation } from '@zenbusiness/application-commons-graphql';

import { MODAL_NAMES } from '#client/constants/modals';
import { TOKEN_TYPES } from '#client/constants/tokens';
import { useLogError } from '#client/hooks/useLogError';
import { UNFREEZE_CARD_MUTATION } from '#client/mutations';

import useCardsContext from './useCardsContext';
import useConstBusinessEntityUuid from './useConstBusinessEntityUuid';
import useCustomerToken from './useCustomerToken';
import useModalContext from './useModalContext';
import useToastContext from './useToastContext';
import useUnitAccountId from './useUnitAccountId';

export default function useUnFreezeCard(id, handleClose) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const { refetchCard, loading: cardsLoading } = useCardsContext();
  const { createSuccessToast, createCommonErrorToast } = useToastContext();
  const { openModal, clearAllModal } = useModalContext();

  const handleCloseModal = handleClose ?? clearAllModal;

  const { getCustomerToken, loading: tokenLoading } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });

  const [unfreezeCard, { loading: unfreezeCardLoading, errors, error: unfreezeError }] =
    useMutation(UNFREEZE_CARD_MUTATION, {
      noThrow: true
    });

  useLogError(errors, unfreezeError);

  const handleUnfreezeCard = async () => {
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      clearAllModal();
      return;
    }

    const { error, data } = await unfreezeCard({
      variables: {
        input: {
          identifier: {
            businessUuid: businessEntityUuid,
            unitAccountId
          },
          id,
          customerToken
        }
      }
    });
    if (!error && data) {
      await refetchCard();
      handleCloseModal();
      createSuccessToast({
        title: `Your card ending in ${data?.unfreezeCard?.last4Digits} is ready to use`,
        secondaryAction: {
          label: 'Undo',
          size: 'sm',
          onClick: () => {
            openModal({
              name: MODAL_NAMES.FREEZE_CARD_MODAL
            });
          }
        }
      });
    } else {
      createCommonErrorToast({ error });
    }
  };

  return {
    handleUnfreezeCard,
    loading: unfreezeCardLoading || cardsLoading || tokenLoading
  };
}
